import {
    IvrFlow,
    OfferConfig,
    OfferLanguageEntry,
    ProcessOfferAudioFileInput,
} from "../pages/admin/OfferLanguageConfiguration/types";
import { adminService } from "./admin-services";
import { getUserId } from "./auth-service";

const DEFAULT_FLOW_ID = "914";

type UploadWavApiResponse = {
    Status?: string;
    Message?: string;
    Data?: {
        url?: string;
    };
};

type OotdApiLanguages = Record<string, Record<string, { package_id: string; audio_file: string }>>;

type OotdApiOffer = {
    offer_name: string;
    valid_from: string;
    valid_from_time?: string;
    valid_till: string;
    valid_till_time?: string;
    brand: string;
    flowId: string | number;
    languages: OotdApiLanguages;
};

type OotdGetFlowsApiResponse = {
    status?: string;
    message?: string;
    data?: Array<{
        flowId?: string | number;
        flowName?: string;
    }>;
};

type OotdGetOffersApiResponse = {
    status?: string;
    message?: string;
    data?: {
        flowId?: string | number;
        offers?: Record<string, OotdApiOffer>;
    };
};

type OotdGetOfferApiResponse = {
    status?: string;
    message?: string;
    flowId?: string | number;
    offer_name?: string;
    is_active_now?: boolean;
    data?: OotdApiOffer;
};

type OotdApiResponse = {
    status?: string;
    message?: string;
    data?: unknown;
};

function isSuccessfulApiStatus(status?: string): boolean {
    const normalizedStatus = (status || "").trim().toLowerCase();
    if (!normalizedStatus) return false;
    return normalizedStatus === "success" || /\bsuccess(?:ful|fully)?\b/.test(normalizedStatus);
}

function resolveFlowId(flowId?: string | number) {
    if (flowId === null || flowId === undefined || `${flowId}`.trim().length === 0) {
        return DEFAULT_FLOW_ID;
    }
    return String(flowId);
}

function normalizeTimeValue(timeValue: string): string {
    const value = (timeValue || "").trim();
    if (!value) return "";
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{2}:\d{2}:\d{2}/.test(value)) return value.slice(0, 8);
    return value;
}

function getDateAndTimeParts(dateValue: string, timeValue = ""): { date: string; time: string } {
    const rawDateValue = (dateValue || "").trim();
    const rawTimeValue = normalizeTimeValue(timeValue);

    if (rawDateValue && rawTimeValue) {
        return { date: rawDateValue, time: rawTimeValue };
    }

    if (!rawDateValue) {
        return { date: "", time: rawTimeValue };
    }

    if (rawDateValue.includes("T")) {
        const [datePart, timePartRaw = ""] = rawDateValue.split("T");
        const timePart = timePartRaw.replace("Z", "").split(".")[0];
        return {
            date: datePart,
            time: rawTimeValue || normalizeTimeValue(timePart),
        };
    }

    if (rawDateValue.includes(" ")) {
        const [datePart, timePartRaw = ""] = rawDateValue.split(" ");
        return {
            date: datePart,
            time: rawTimeValue || normalizeTimeValue(timePartRaw),
        };
    }

    return {
        date: rawDateValue,
        time: rawTimeValue,
    };
}

function normalizeBrand(brand: string): "D2H" | "DISH" {
    const normalized = (brand || "").toUpperCase();
    if (normalized === "D2H") return "D2H";
    return "DISH";
}

function transformApiOfferToFrontend(
    apiOffer: OotdApiOffer,
    fallbackOfferName = "",
    fallbackFlowId: string | number = DEFAULT_FLOW_ID
): OfferConfig {
    const languages: OfferLanguageEntry[] = Object.entries(apiOffer.languages || {}).map(
        ([languageName, variantsObj]) => ({
            language_name: languageName,
            variants: Object.entries(variantsObj).map(([boxName, data]) => ({
                id: crypto.randomUUID(),
                box_name: boxName,
                package_id: data.package_id || "",
                audio_file: data.audio_file || "",
                audioUrl: data.audio_file || "",
            })),
        })
    );

    const offerName = (apiOffer.offer_name || fallbackOfferName || "").trim();
    const validFrom = getDateAndTimeParts(apiOffer.valid_from, apiOffer.valid_from_time || "");
    const validTill = getDateAndTimeParts(apiOffer.valid_till, apiOffer.valid_till_time || "");

    return {
        offer_id: offerName,
        offer_name: offerName,
        flow_id: String(apiOffer.flowId || fallbackFlowId),
        valid_from: validFrom.date,
        valid_from_time: validFrom.time,
        valid_till: validTill.date,
        valid_till_time: validTill.time,
        brand: normalizeBrand(apiOffer.brand),
        languages,
    };
}

function transformFrontendOfferToApi(offer: OfferConfig): OotdApiOffer {
    const validFrom = getDateAndTimeParts(offer.valid_from, offer.valid_from_time);
    const validTill = getDateAndTimeParts(offer.valid_till, offer.valid_till_time);
    const languages: OotdApiLanguages = {};

    for (const lang of offer.languages) {
        const variantsObj: Record<string, { package_id: string; audio_file: string }> = {};
        for (const variant of lang.variants) {
            variantsObj[variant.box_name] = {
                package_id: variant.package_id.trim(),
                audio_file: variant.audioUrl || variant.audio_file,
            };
        }
        languages[lang.language_name] = variantsObj;
    }

    return {
        offer_name: offer.offer_name,
        valid_from: validFrom.date,
        valid_from_time: validFrom.time,
        valid_till: validTill.date,
        valid_till_time: validTill.time,
        brand: offer.brand,
        flowId: offer.flow_id,
        languages,
    };
}

export async function getOotdFlows(): Promise<IvrFlow[]> {
    const payload = await adminService({
        urlParams: "api/dishtv/ootd/setoffer/get",
        method: "POST",
        bodyParams: {
            UserId: getUserId(),
        },
    }) as OotdGetFlowsApiResponse;

    if (payload?.status !== "success" || !Array.isArray(payload?.data)) {
        throw new Error(payload?.message || "Failed to fetch IVR flows.");
    }

    return payload.data
        .map((flow) => ({
            flowId: String(flow.flowId ?? ""),
            flowName: (flow.flowName || "").trim(),
        }))
        .filter((flow) => flow.flowId.trim().length > 0 && flow.flowName.length > 0);
}

export async function getOffersByFlowId(flowId: string): Promise<OfferConfig[]> {
    const resolvedFlowId = resolveFlowId(flowId);
    const payload = await adminService({
        urlParams: "api/dishtv/ootd/get",
        method: "POST",
        bodyParams: {
            flowId: resolvedFlowId,
            UserId: getUserId(),
        },
    }) as OotdGetOffersApiResponse;

    if (payload?.status !== "success") {
        throw new Error(payload?.message || "Failed to fetch offers.");
    }

    if (!payload?.data?.offers) {
        return [];
    }

    const offers = payload.data.offers;
    const responseFlowId = payload.data.flowId || resolvedFlowId;

    return Object.entries(offers).map(([offerKey, apiOffer]) =>
        transformApiOfferToFrontend(apiOffer, offerKey, responseFlowId)
    );
}

export async function getOfferByFlowAndName(flowId: string, offerName: string): Promise<OfferConfig> {
    const trimmedOfferName = (offerName || "").trim();
    if (!trimmedOfferName) {
        throw new Error("Offer name is required.");
    }

    const resolvedFlowId = resolveFlowId(flowId);
    const payload = await adminService({
        urlParams: "api/dishtv/ootd/getoffer",
        method: "POST",
        bodyParams: {
            flowId: resolvedFlowId,
            offer_name: trimmedOfferName,
            UserId: getUserId(),
        },
    }) as OotdGetOfferApiResponse;

    if (payload?.status !== "success" || !payload?.data) {
        throw new Error(payload?.message || "Failed to fetch offer details.");
    }

    const offer = transformApiOfferToFrontend(
        payload.data,
        payload.offer_name || trimmedOfferName,
        payload.flowId || resolvedFlowId
    );

    if (typeof payload.is_active_now === "boolean") {
        offer.is_active = payload.is_active_now;
    }

    return offer;
}

export async function createOffer(
    payload: OfferConfig
): Promise<{ Status: string; Message: string; Data: OfferConfig }> {
    const apiPayload = {
        ...transformFrontendOfferToApi(payload),
        UserId: getUserId(),
    };

    const result = await adminService({
        urlParams: "api/dishtv/ootd/set",
        method: "POST",
        bodyParams: apiPayload,
    }) as OotdApiResponse;

    if (!isSuccessfulApiStatus(result?.status)) {
        throw new Error(result?.message || "Failed to create offer.");
    }

    return {
        Status: "Success",
        Message: result.message || result.status || "Offer created successfully.",
        Data: { ...payload, offer_id: payload.offer_id || payload.offer_name },
    };
}

export async function updateOffer(
    payload: OfferConfig
): Promise<{ Status: string; Message: string; Data: OfferConfig }> {
    const apiPayload = {
        ...transformFrontendOfferToApi(payload),
        UserId: getUserId(),
    };

    const result = await adminService({
        urlParams: "api/dishtv/ootd/update",
        method: "POST",
        bodyParams: apiPayload,
    }) as OotdApiResponse;

    if (!isSuccessfulApiStatus(result?.status)) {
        throw new Error(result?.message || "Failed to update offer.");
    }

    return {
        Status: "Success",
        Message: result.message || "Offer updated successfully.",
        Data: payload,
    };
}

const BRAND_UPLOAD_MAP: Record<string, string> = {
    DISH: "DISHTV",
    D2H: "D2H",
};

export async function processOfferAudioFile({
    flowId,
    language,
    variant,
    brand,
    file,
}: ProcessOfferAudioFileInput): Promise<{ audioUrl: string; fileName: string }> {
    const validExtension = /\.wav$/i.test(file.name);

    if (!validExtension) {
        throw new Error("Only WAV files are supported.");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("flowId", resolveFlowId(flowId));
    formData.append("language", language);
    formData.append("quality", variant);
    formData.append("brand", BRAND_UPLOAD_MAP[brand] || brand);

    const payload = await adminService({
        urlParams: "api/files/upload/wav",
        method: "POST",
        bodyParams: formData,
        isFormData: true,
    }) as UploadWavApiResponse;

    const uploadedUrl = payload?.Data?.url;
    const isSuccess = payload?.Status === "Success" && Boolean(uploadedUrl);

    if (!isSuccess) {
        throw new Error(payload?.Message || "Audio upload failed.");
    }

    const audioUrl = uploadedUrl as string;

    return {
        audioUrl,
        fileName: file.name,
    };
}
